import { useState } from "react";
import useSWR, { KeyedMutator } from "swr";
import { useLocale, useTranslations } from "next-intl";
//@ts-expect-error
import { decode } from "he";
import { motion, AnimatePresence } from "framer-motion";
import { FireIcon, LockClosedIcon } from "@heroicons/react/20/solid";
import { CalendarIcon } from "@heroicons/react/24/outline";
import { bookedShowingTickets, handleShowingType } from "../utils/helpers";
import {
  EventData,
  EventShowing,
  EventShowingsMsgs,
  ShowingMessage,
} from "@types";
import { Basket } from "@features/cartDetails/types";
import {
  joinClassNames,
  getCookieConsent,
  getCookieValue,
  createBasketSession,
  formatPrice,
} from "@utils/helpers";
import {
  useIsShopsite,
  useRequiresCookieConsent,
} from "@context/DomainContext";
import { AccessType, ShowingStatus } from "@utils/enums";
import { Badge, Card, Flex, Heading, Text } from "@components/theming";
import ShowingCountDown from "@features/eventPage/components/showingCountDown";
import ShowingStatusToolTip from "./showingStatusToolTip";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@components/accordion";
import ConnectedShowings from "./connectedShowings";
import { EventAreaPicker } from "@features/eventAreaPicker";
import { AreaFormatted } from "@features/eventAreaPicker/types";
import { useLatestCookieValue } from "@utils/hooks";
import { fetcher } from "@utils/fetchers";
import { EventConfigOverrides } from "@utils/flags";
import LoadingSpinner from "@components/loadingSpinner";
import Button from "@components/theming/button";

export default function ShowingAccordionItem({
  activeShowings,
  index,
  numShowings,
  showing,
  messages,
  event,
  mutate,
  getPriceRange,
  isCombo,
  onAddToCart,
  eventConfigOverrides,
}: {
  activeShowings: EventShowing[];
  index: number;
  numShowings: number;
  showing: EventShowing;
  messages?: ShowingMessage[];
  event: EventData;
  mutate: KeyedMutator<EventShowingsMsgs>;
  getPriceRange: ({
    pricesectionIds,
    groupedPricesectionIds,
  }: {
    pricesectionIds?: number[];
    groupedPricesectionIds?: number[];
  }) => { min: number; max: number; isEmpty?: boolean };
  isCombo?: boolean;
  onAddToCart?: () => void;
  eventConfigOverrides?: EventConfigOverrides;
}) {
  const t = useTranslations("EventPage");
  const statusTranslations = useTranslations("EventPage.statuses");
  const isShopsite = useIsShopsite();
  const cookieConsentRequired = useRequiresCookieConsent();
  // if user does not consent to cookies, don't pass analytics configs down to children
  // so analytics calls won't accidentally be triggered
  const consent = getCookieConsent();
  const basketKey = useLatestCookieValue("ASP.NET_SessionId");
  const locale = useLocale();
  const showingComboConfig = showing.config.showingcomboconfig;

  const [requestingBasket, setRequestingBasket] = useState(false);
  const [lastAreaSelected, setLastAreaSelected] =
    useState<AreaFormatted | null>(null);
  const [selectedConnectedShowings, setSelectedConnectedShowings] = useState<
    number[]
  >(
    showingComboConfig?.connectedShowings &&
      showingComboConfig.connectedShowings.length ===
        showingComboConfig.minShowings
      ? showingComboConfig.connectedShowings
      : []
  );
  const [basketMaxRetriesHit, setBasketMaxRetriesHit] = useState(false);

  const { data: basket } = useSWR<Basket>(
    basketKey ? `basket/${basketKey}` : null,
    fetcher
  );

  const eventId = event.legacy.e_id;
  const basketEvent = basket?.Events?.find((event) => event.Id === eventId);
  const eventVenueId = event.legacy.ev_id;
  const showingConfig = handleShowingType(
    showing,
    messages,
    statusTranslations
  );
  const isSelectable = showingConfig?.selectable;
  const bookedTickets = bookedShowingTickets(showing.id, basketEvent);

  // Matches the accordion's open animation + content reveal fade (see AccordionContent
  // animationClassName below) so a basket creation response can't swap in the ticket
  // picker mid-open-animation, which causes Radix to recompute the content height while
  // the height keyframe is still running and makes the accordion look like it plays its
  // opening animation twice. Only applies when we actually have to create a basket -
  // if a session already exists we skip the loading state entirely (see below).
  const MIN_OPEN_ANIMATION_MS = 350;

  async function createBasket() {
    if (!requestingBasket) {
      if (getCookieValue("ASP.NET_SessionId")) {
        // Basket already exists - go straight to the ticket picker, no loading state.
        return;
      }
      setRequestingBasket(true);
      const start = Date.now();
      const key = await createBasketSession(60);
      const remaining = MIN_OPEN_ANIMATION_MS - (Date.now() - start);
      if (remaining > 0) {
        await new Promise((resolve) => setTimeout(resolve, remaining));
      }
      setBasketMaxRetriesHit(key === "max_retries");
      setRequestingBasket(key === "max_retries" ? false : !key);
    }
  }

  const gaPriceRange = getPriceRange({
    groupedPricesectionIds: showing.area.ga.map((ga) => ga.pricesection.id),
  });

  if (!isSelectable) {
    return (
      <motion.div
        key={showing.id}
        className={joinClassNames(
          "relative flex flex-row items-center gap-4 px-4 sm:px-6 py-5 w-full border-b",
          index === 0 ? "max-sm:!border-t" : "",
          index === numShowings - 1 && isShopsite
            ? "sm:!border-b-0"
            : index === numShowings - 1
            ? "!border-b-0"
            : "",
          isShopsite
            ? "bg-gray-100"
            : "border-[color:var(--gray-5)] bg-[color:var(--gray-a3)]"
        )}
        variants={{ hidden: { opacity: 0 }, show: { opacity: 1 } }}
      >
        <div className="flex items-start gap-x-4 w-full">
          <div className="relative flex items-center justify-center w-8 h-5 -mt-0.5">
            <CalendarIcon
              className={joinClassNames(
                "h-6 w-6 flex-none rounded bg-transparent",
                isShopsite ? "" : "text-[color:var(--gray-11)]"
              )}
            />
            {showing.status.id === ShowingStatus.SOLDOUT ||
            showing.status.id === ShowingStatus.ADMINSOLDOUT ? (
              <span
                style={{ fontSize: "0.5rem" }}
                className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 leading-none bg-red-400 text-white pt-0.5 pb-px px-0.5 w-max -rotate-6"
              >
                {t("soldout")}
              </span>
            ) : showing.access.type === AccessType.PASSWORD ? (
              <LockClosedIcon
                className={joinClassNames(
                  "absolute -bottom-1 -right-1 h-4 w-4 stroke-2 text-yellow-500",
                  isShopsite ? "bg-gray-100" : "bg-[color:var(--gray-5)]"
                )}
              />
            ) : showing.status.hot ? (
              <FireIcon
                className={joinClassNames(
                  "absolute -bottom-1 -right-1 h-4 w-4 stroke-2 text-orange-400",
                  isShopsite ? "bg-gray-100" : "bg-[color:var(--gray-5)]"
                )}
              />
            ) : (
              <></>
            )}
          </div>
          <div className="flex flex-row items-start justify-between flex-wrap gap-x-4 gap-y-3.5 w-full">
            <div className="text-left">
              <div className="flex max-sm:flex-col sm:items-center gap-2">
                <Text
                  as="p"
                  size="2"
                  weight="bold"
                  whitelabelClasses="text-[color:var(--gray-11)]"
                  shopsiteClasses="text-sm font-semibold"
                >
                  {showing.date.display
                    ? showing.date.display
                    : showing.date.local.formatted}
                </Text>
                {!gaPriceRange.isEmpty && !isShopsite && (
                  <Badge color="gray" whitelabelClasses="w-fit">
                    <span className="text-xs">
                      {gaPriceRange.min !== gaPriceRange.max ? (
                        <>
                          {formatPrice(
                            locale,
                            event.currency.code,
                            gaPriceRange.min
                          )}{" "}
                          -{" "}
                        </>
                      ) : (
                        <></>
                      )}
                      {formatPrice(
                        locale,
                        event.currency.code,
                        gaPriceRange.max
                      )}
                    </span>
                  </Badge>
                )}
              </div>
              {showing.name ? (
                <Text
                  as="p"
                  size="2"
                  weight="bold"
                  sharedClasses="w-fit mt-1"
                  whitelabelClasses="text-[color:var(--accent-8)]"
                  shopsiteClasses="text-xs leading-5 text-gray-500"
                >
                  {decode(showing.name)}
                </Text>
              ) : (
                <></>
              )}
            </div>
            <Badge
              size="3"
              sharedClasses="-mt-1.5 -ml-2"
              shopsiteClasses="gap-1 !py-1.5 !rounded-md whitespace-nowrap font-medium"
              color={
                showingConfig.color as "red" | "green" | "orange" | undefined
              }
            >
              {showing.status.id === ShowingStatus.AVAILABLE &&
              showing.access.type === AccessType.NOTYETONSALE ? (
                <ShowingCountDown
                  ttl={showing.access.date.public.secondstostart}
                  title={showingConfig?.title ?? ""}
                  mutate={mutate}
                  textColor={
                    isShopsite
                      ? "text-orange-600"
                      : "text-[color:var(--orange-a11)]"
                  }
                  loadingColor={isShopsite ? "#ea580c" : "var(--orange-a11)"}
                />
              ) : (
                <Text as="span" shopsiteClasses={showingConfig?.textColor}>
                  {showingConfig?.title}
                </Text>
              )}
              {showingConfig.description?.trim() ? (
                <ShowingStatusToolTip
                  statusId={showing.status.id}
                  accessType={showing.access.type}
                  title={showingConfig.title ?? ""}
                  description={showingConfig.description ?? ""}
                />
              ) : (
                <></>
              )}
            </Badge>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <AccordionItem
      asChild
      key={showing.id}
      value={showing.id.toString()}
      className={joinClassNames(
        index === 0 ? "max-sm:!border-t" : "",
        index === numShowings - 1 && isShopsite && numShowings > 1
          ? "sm:!border-b-0"
          : index === numShowings - 1
          ? "!border-b-0"
          : ""
      )}
      whitelabelClasses="border-[color:var(--gray-5)]"
    >
      <motion.div
        className="relative"
        variants={{ hidden: { opacity: 0 }, show: { opacity: 1 } }}
      >
        <Flex
          sharedClasses="flex flex-col md:flex-row items-start md:items-center justify-between gap-2"
          whitelabelClasses={
            numShowings > 1 ? "hover:bg-[color:var(--gray-a2)]" : ""
          }
          shopsiteClasses={numShowings > 1 ? "hover:bg-gray-50" : ""}
        >
          <AccordionTrigger
            data-testid={`eventlist-${showing.id}`}
            disabled={numShowings === 1 || !isSelectable}
            onClick={() => {
              createBasket();
            }}
            sharedClasses="relative group flex w-full justify-between px-4 py-5 sm:px-6 gap-2 flex-grow"
            shopsiteClasses={joinClassNames(
              "gap-x-6",
              numShowings > 1 ? "disabled:bg-gray-100" : ""
            )}
            whitelabelClasses={
              numShowings > 1 ? "disabled:bg-[color:var(--gray-a2)]" : ""
            }
          >
            <div
              className={joinClassNames(
                "relative flex items-start w-full justify-between",
                isShopsite ? "gap-x-4" : "gap-2"
              )}
            >
              <div className="flex items-start gap-x-4">
                <div className="relative flex items-center justify-center w-8 h-5">
                  <CalendarIcon
                    className={joinClassNames(
                      "h-6 w-6 flex-none rounded bg-transparent",
                      isShopsite ? "" : "text-[color:var(--gray-11)]"
                    )}
                  />
                  {showing.status.id === ShowingStatus.SOLDOUT ||
                  showing.status.id === ShowingStatus.ADMINSOLDOUT ? (
                    <span
                      style={{ fontSize: "0.5rem" }}
                      className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 leading-none bg-red-400 text-white pt-0.5 pb-px px-0.5 w-max -rotate-6"
                    >
                      {t("soldout")}
                    </span>
                  ) : showing.access.type === AccessType.PASSWORD ? (
                    <LockClosedIcon
                      className={joinClassNames(
                        "absolute -bottom-1 -right-1 h-4 w-4 stroke-2 text-yellow-500",
                        isShopsite
                          ? "bg-white"
                          : "bg-[color:var(--gray-2)] group-hover:bg-[color:var(--gray-3)]"
                      )}
                    />
                  ) : showing.status.hot ? (
                    <FireIcon
                      className={joinClassNames(
                        "absolute -bottom-1 -right-1 h-4 w-4 stroke-2 text-orange-400",
                        isShopsite
                          ? "bg-white group-hover:bg-gray-50"
                          : "bg-[color:var(--gray-2)] group-hover:bg-[color:var(--gray-3)]"
                      )}
                    />
                  ) : (
                    <></>
                  )}
                </div>
                <div className="min-w-0 flex-auto text-left">
                  <div className="flex max-sm:flex-col sm:items-center gap-2">
                    <Text
                      as="p"
                      size="2"
                      weight="bold"
                      shopsiteClasses="text-sm font-semibold"
                    >
                      {showing.date.display
                        ? showing.date.display
                        : showing.date.local.formatted}
                    </Text>
                    {!gaPriceRange.isEmpty && !isShopsite && (
                      <Badge color="gray" whitelabelClasses="w-fit">
                        <span className="text-xs">
                          {gaPriceRange.min !== gaPriceRange.max ? (
                            <>
                              {formatPrice(
                                locale,
                                event.currency.code,
                                gaPriceRange.min
                              )}{" "}
                              -{" "}
                            </>
                          ) : (
                            <></>
                          )}
                          {formatPrice(
                            locale,
                            event.currency.code,
                            gaPriceRange.max
                          )}
                        </span>
                      </Badge>
                    )}
                  </div>
                  {showing.name ? (
                    <Text
                      as="p"
                      size="2"
                      weight="bold"
                      sharedClasses="w-fit mt-1"
                      whitelabelClasses="text-[color:var(--accent-11)]"
                      shopsiteClasses="text-xs leading-5 text-gray-500"
                    >
                      {decode(showing.name)}
                    </Text>
                  ) : (
                    <></>
                  )}
                </div>
              </div>
              <AnimatePresence>
                {!!bookedTickets && (
                  <motion.div
                    initial="hide"
                    animate="show"
                    exit="hide"
                    variants={{
                      hide: {
                        opacity: 0,
                      },
                      show: {
                        scale: [0, 1.2, 1],
                        opacity: 1,
                        transition: {
                          scale: {
                            type: "tween",
                            duration: 0.4,
                          },
                        },
                      },
                    }}
                    className="flex h-5 shrink-0"
                  >
                    <Badge shopsiteClasses="!py-1 !rounded-lg">
                      <Text size="1" weight="medium" shopsiteClasses="!text-xs">
                        {t("amountincart", {
                          amount: bookedTickets,
                        })}
                      </Text>
                    </Badge>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </AccordionTrigger>
        </Flex>
        <AccordionContent
          animationClassName="data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up-delayed"
          transitionClassName=""
        >
          <div className="p-4 group-data-[state=open]:animate-content-fade-in group-data-[state=closed]:animate-content-fade-out">
            <ConnectedShowings
              showingcomboconfig={showing.config.showingcomboconfig}
              activeShowings={activeShowings}
              basketEvent={basketEvent}
              eventConfigOverrides={eventConfigOverrides}
              selectedConnectedShowings={selectedConnectedShowings}
              setSelectedConnectedShowings={setSelectedConnectedShowings}
              messages={messages}
            >
              <AnimatePresence mode="wait" initial={false}>
                {basketMaxRetriesHit ? (
                  <motion.div
                    key="maxretries"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                  >
                    <Card>
                      <div className="flex flex-col gap-4 items-center justify-center w-full min-h-[197px]">
                        {isShopsite ? (
                          <div className="flex flex-col gap-1">
                            <Heading
                              as="h3"
                              size="6"
                              weight="bold"
                              sharedClasses="text-center mx-4"
                              shopsiteClasses="text-xl font-semibold text-gray-900"
                            >
                              {t("error.maxretriesheading")}
                            </Heading>
                            <Text
                              size="4"
                              weight="medium"
                              whitelabelClasses="text-[color:var(--accent-11)]"
                              sharedClasses="text-center"
                              shopsiteClasses="text-lg font-medium text-gray-600"
                            >
                              {t("error.maxretriesmessage")}
                            </Text>
                          </div>
                        ) : (
                          <div className="flex flex-col gap-1">
                            <Heading
                              as="h3"
                              size="6"
                              weight="bold"
                              sharedClasses="text-center mx-4"
                            >
                              {t("error.maxretriesheadingwl")}
                            </Heading>
                            <Text
                              size="4"
                              weight="medium"
                              whitelabelClasses="text-[color:var(--accent-11)]"
                              sharedClasses="text-center"
                            >
                              {t("error.maxretriesmessagewl")}
                            </Text>
                          </div>
                        )}
                        <Button
                          onClick={() => {
                            setBasketMaxRetriesHit(false);
                            createBasket();
                          }}
                        >
                          {t("error.maxretriesbutton")}
                        </Button>
                      </div>
                    </Card>
                  </motion.div>
                ) : requestingBasket ? (
                  <motion.div
                    key="loading"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                  >
                    <Card>
                      <div className="flex flex-col gap-4 items-center justify-center w-full min-h-[197px]">
                        {isShopsite ? (
                          <div className="flex flex-col gap-1">
                            <Heading
                              as="h3"
                              size="6"
                              weight="bold"
                              sharedClasses="text-center mx-4"
                              shopsiteClasses="text-xl font-semibold text-gray-900"
                            >
                              {t("error.retryheading")}
                            </Heading>
                            <Text
                              size="4"
                              weight="medium"
                              whitelabelClasses="text-[color:var(--accent-11)]"
                              sharedClasses="text-center"
                              shopsiteClasses="text-lg font-medium text-gray-600"
                            >
                              {t("error.retrymessage")}
                            </Text>
                          </div>
                        ) : (
                          <div className="flex flex-col gap-1">
                            <Heading
                              as="h3"
                              size="6"
                              weight="bold"
                              sharedClasses="text-center mx-4"
                            >
                              {t("error.retryheadingwl")}
                            </Heading>
                            <Text
                              size="4"
                              weight="medium"
                              whitelabelClasses="text-[color:var(--accent-11)]"
                              sharedClasses="text-center"
                            >
                              {t("error.retrymessagewl")}
                            </Text>
                          </div>
                        )}
                        <LoadingSpinner
                          size="32"
                          color={isShopsite ? undefined : "var(--gray-11)"}
                        />
                      </div>
                    </Card>
                  </motion.div>
                ) : (
                  <motion.div
                    key="content"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                  >
                    <EventAreaPicker
                      eventId={eventId}
                      eventName={event.name}
                      eventVenueId={eventVenueId}
                      orgUid={event.organisation.uid}
                      currency={event.currency}
                      showing={showing}
                      messages={messages ?? []}
                      basketEvent={basketEvent}
                      getPriceRange={getPriceRange}
                      pricesection={
                        event.pricing.priceschemes?.find(
                          (ps) => ps.id === showing.config.pricescheme.id
                        )?.pricesections
                      }
                      lastAreaSelected={lastAreaSelected}
                      setLastAreaSelected={setLastAreaSelected}
                      createBasket={createBasket}
                      selectedConnectedShowings={
                        isCombo ? selectedConnectedShowings : null
                      }
                      analyticsConfigs={
                        event.analyticsConfigs &&
                        (!cookieConsentRequired || consent.marketing === true)
                          ? event.analyticsConfigs
                          : []
                      }
                      landingPages={event.landingpages}
                      onAddToCart={onAddToCart}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </ConnectedShowings>
          </div>
        </AccordionContent>
      </motion.div>
    </AccordionItem>
  );
}
